import React from 'react';
import { Link } from 'react-router-dom';
import { useExpensesStats } from '../../hooks/useExpenses';
import { useUsers } from '../../hooks/useUsers';
import { formatCurrency } from '../../utils/formatters';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Receipt,
  DollarSign,
  Clock,
  Plus,
  ArrowRight,
} from 'lucide-react';

const Dashboard = () => {
  const { data: statsData, isLoading: statsLoading, error: statsError } = useExpensesStats();
  const { data: usersData, isLoading: usersLoading, error: usersError } = useUsers();

  if (statsLoading || usersLoading) {
    return <Loading text="Carregando dashboard..." />;
  }

  if (statsError || usersError) {
    return <ErrorMessage message="Erro ao carregar dados do dashboard." />;
  }

  const stats = statsData || {};
  const users = usersData?.data || [];

  const activeUsers = users.filter((u) => u.status === 'active').length;
  const pendingUsers = users.filter((u) => u.status === 'pending').length;

  const statCards = [
    {
      title: 'Total Aprovado',
      value: formatCurrency(stats.totalApproved || 0),
      icon: DollarSign,
      color: 'bg-green-500',
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'Despesas Pendentes',
      value: formatCurrency(stats.totalPending || 0),
      icon: Clock,
      color: 'bg-yellow-500',
      count: `${stats.countPending || 0} itens`,
    },
    {
      title: 'Usuários Ativos',
      value: activeUsers,
      icon: Users,
      color: 'bg-blue-500',
      count: `${pendingUsers} pendentes`,
    },
    {
      title: 'Mês Atual',
      value: formatCurrency(stats.totalCurrentMonth || 0),
      icon: TrendingUp,
      color: 'bg-purple-500',
      count: new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    },
  ];

  const quickActions = [
    {
      title: 'Nova Despesa',
      description: 'Registrar nova despesa',
      icon: Receipt,
      link: '/admin/despesas/nova',
      color: 'bg-primary-600',
    },
    {
      title: 'Novo Usuário',
      description: 'Cadastrar novo usuário',
      icon: Users,
      link: '/admin/usuarios/novo',
      color: 'bg-green-600',
    },
  ];

  const topCategories = Object.entries(stats.byCategory || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Visão geral das finanças e usuários da associação
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`${stat.color} h-12 w-12 rounded-lg flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                {stat.trend && (
                  <div className={`flex items-center text-sm font-medium ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`}>
                    {stat.trendUp ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                    {stat.trend}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                {stat.count && (
                  <p className="text-xs text-gray-500 mt-1">{stat.count}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <Link
                key={index}
                to={action.link}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`${action.color} h-12 w-12 rounded-lg flex items-center justify-center`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{action.title}</h3>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                  </div>
                  <Plus className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Categories */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Principais Categorias
            </h2>
            <Link
              to="/admin/despesas"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
            >
              Ver todas
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          {topCategories.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nenhuma despesa registrada ainda
            </p>
          ) : (
            <div className="space-y-4">
              {topCategories.map(([category, amount]) => (
                <div key={category}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {category}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{
                        width: `${(amount / (stats.totalApproved || 1)) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Atividade Recente
            </h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Receipt className="h-4 w-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{stats.countApproved || 0}</span> despesas aprovadas
                </p>
                <p className="text-xs text-gray-500">Total: {formatCurrency(stats.totalApproved || 0)}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="h-4 w-4 text-yellow-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{stats.countPending || 0}</span> despesas pendentes
                </p>
                <p className="text-xs text-gray-500">Aguardando aprovação</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{activeUsers}</span> usuários ativos
                </p>
                <p className="text-xs text-gray-500">{pendingUsers} aguardando aprovação</p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <Link
              to="/admin/despesas"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center"
            >
              Ver todas as atividades
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

